import { useEffect, useState } from "react";
import DashboardLayout, { StatCard, EmptyState } from "./DashboardLayout";
import { LayoutDashboard, Users, ClipboardCheck as MessageSquareCheck, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";

const NAV = [
    { key: "queue", label: "Review Queue", icon: MessageSquareCheck },
    { key: "mentees", label: "Mentees", icon: Users },
];

export default function MentorDashboard() {
    const [tab, setTab] = useState("queue");
    const [data, setData] = useState(null);
    const [fb, setFb] = useState({});

    const load = () => api.get("/mentor/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
    useEffect(() => { load(); }, []);

    const review = async (id, decision) => {
        try {
            await api.post(`/mentor/review/${id}`, { decision, feedback: fb[id] || "", score: decision === "approve" ? 4 : 2 });
            toast.success(decision === "approve" ? "Project approved!" : "Feedback sent to learner.");
            load();
        } catch { toast.error("Could not submit review."); }
    };

    if (!data) return <DashboardLayout nav={NAV} active={tab} onNav={setTab} title="Mentor Dashboard"><div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></DashboardLayout>;

    return (
        <DashboardLayout nav={NAV} active={tab} onNav={setTab} title="Mentor Dashboard">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <StatCard icon={MessageSquareCheck} label="In Review Queue" value={(data.review_queue || []).length} />
                <StatCard icon={Users} label="Mentees" value={(data.mentees || []).length} color="accent" />
            </div>
            {tab === "queue" && (
                <div className="space-y-4">
                    {(data.review_queue || []).length ? data.review_queue.map((p) => (
                        <Card key={p.id} className="p-5" data-testid={`review-${p.id}`}>
                            <div className="flex items-center justify-between"><h3 className="font-heading font-bold">{p.title}</h3><Badge className="bg-accent/15 text-accent border-0">{p.status}</Badge></div>
                            <p className="mt-1 text-sm text-foreground/60">{p.scenario}</p>
                            {p.explanation && (
                                <div className="mt-3 rounded-lg bg-primary/5 border border-primary/15 p-3 text-sm space-y-1" data-testid={`explanation-${p.id}`}>
                                    <p className="font-heading font-semibold text-primary">Explain Your Work</p>
                                    {p.explanation.what && <p><b>What:</b> {p.explanation.what}</p>}
                                    {p.explanation.ai_tools && <p><b>AI tools used:</b> {p.explanation.ai_tools}</p>}
                                    {p.explanation.problems && <p><b>Problems solved:</b> {p.explanation.problems}</p>}
                                    {p.explanation.learned && <p><b>Learned:</b> {p.explanation.learned}</p>}
                                    {p.explanation.video_url && <a href={p.explanation.video_url} target="_blank" rel="noreferrer" className="text-primary underline">▶ Video explanation</a>}
                                </div>
                            )}
                            <Textarea placeholder="Write feedback…" className="mt-3" rows={2} value={fb[p.id] || ""} onChange={(e) => setFb((f) => ({ ...f, [p.id]: e.target.value }))} data-testid={`feedback-${p.id}`} />
                            <div className="mt-3 flex gap-2">
                                <Button size="sm" className="rounded-full" data-testid={`approve-${p.id}`} onClick={() => review(p.id, "approve")}>Approve</Button>
                                <Button size="sm" variant="outline" className="rounded-full" data-testid={`revise-${p.id}`} onClick={() => review(p.id, "revision")}>Request Revision</Button>
                                <Button size="sm" variant="ghost" data-testid={`feedback-btn-${p.id}`} onClick={() => review(p.id, "feedback")}>Send Feedback</Button>
                            </div>
                        </Card>
                    )) : <EmptyState icon={MessageSquareCheck} title="Review queue is empty" desc="Submitted projects awaiting your review will appear here." />}
                </div>
            )}
            {tab === "mentees" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {(data.mentees || []).map((m) => (
                        <Card key={m.id} className="p-5 flex items-center gap-3"><span className="grid h-11 w-11 place-items-center rounded-full bg-primary/10 text-primary font-heading font-bold">{m.name[0]}</span><div><p className="font-medium">{m.name}</p><p className="text-sm text-foreground/50">{m.email}</p></div></Card>
                    ))}
                </div>
            )}
        </DashboardLayout>
    );
}
